import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Wand2, Search, Database, Save, Loader2, Globe, Layers } from "lucide-react";
import { joinList, splitList, errMsg } from "./utils";
import { usd, overBudget } from "./CostCard";

const init = (p) => ({
  queries: (p.queries || []).join("\n"),
  job_titles: joinList(p.job_titles),
  exclude_titles: joinList(p.exclude_titles),
  page_size: p.page_size || 20,
});

export const QueryPanel = ({ project, busy, onUpdated }) => {
  const [f, setF] = useState(init(project));
  const [aiLoading, setAiLoading] = useState(false);
  useEffect(() => setF(init(project)), [project.id, project.queries, project.job_titles]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    const body = {
      queries: f.queries.split("\n").map((q) => q.trim()).filter(Boolean),
      job_titles: splitList(f.job_titles),
      exclude_titles: splitList(f.exclude_titles),
      page_size: Number(f.page_size) || 20,
    };
    const { data } = await api.patch(`/agency/projects/${project.id}`, body);
    onUpdated(data);
    return data;
  };

  const suggest = async () => {
    if (overBudget(project.cost, project.cost?.queries.usd)) return toast.error("Εκτός budget — αυξήστε το budget στο Brief");
    if (!window.confirm(`Πρόταση queries από AI: κόστος ~${usd(project.cost?.queries.usd)}. Συνέχεια;`)) return;
    setAiLoading(true);
    try {
      const { data } = await api.post(`/agency/projects/${project.id}/generate-queries`);
      onUpdated(data);
      toast.success("Το AI πρότεινε queries & ρόλους");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAiLoading(false);
    }
  };

  const run = async (source) => {
    try {
      const saved = await save();
      const c = saved.cost;
      const ex = source === "explee" || source === "both";
      const web = source === "web" || source === "both";
      const firstStep = (ex ? c.search.usd / c.search.calls : 0) + (web ? 0.15 : 0);
      if (source !== "cache" && overBudget(c, firstStep)) return toast.error("Εκτός budget — αυξήστε το budget στο Brief");
      const lines = [
        web && `Internet (AI web search): ${c.web.calls} αναζητήσεις + LinkedIn για ~${c.web.leads} εταιρείες → ~${usd(c.web.usd)}`,
        ex && `Explee: ${c.search.calls} κλήσεις · έως ~${c.search.credits} credits → ~${usd(c.search.usd)}`,
      ].filter(Boolean);
      const total = (web ? c.web.usd : 0) + (ex ? c.search.usd : 0);
      if (source !== "cache" && !window.confirm(`${lines.join("\n")}\n\nΣύνολο έως ~${usd(total)}${c.budget_usd ? `\nΥπόλοιπο budget: ${usd(c.budget_left_usd)} — σταματά αυτόματα στο όριο.` : ""}\n\nΣυνέχεια;`)) return;
      await api.post(`/agency/projects/${project.id}/run`, { source });
      onUpdated({ ...saved, status: "running" });
      toast.info("Η αναζήτηση ξεκίνησε");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="agency-query-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">Ένα query ανά γραμμή — περιγράφει τον τύπο εταιρείας-αγοραστή (στα Αγγλικά).</p>
        <Button variant="outline" size="sm" onClick={suggest} disabled={aiLoading || busy} data-testid="agency-ai-queries-btn">
          {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Πρόταση από AI
        </Button>
      </div>
      <Textarea rows={6} value={f.queries} onChange={set("queries")} className="font-mono text-sm" data-testid="agency-queries-input" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Ρόλοι-στόχοι (κόμμα)</Label>
          <Input value={f.job_titles} onChange={set("job_titles")} data-testid="agency-job-titles-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Αποτελέσματα / query / χώρα</Label>
          <Input type="number" min={10} max={100} value={f.page_size} onChange={set("page_size")} data-testid="agency-page-size-input" />
        </div>
        <div className="space-y-1.5 md:col-span-3">
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Εξαίρεση ρόλων (κόμμα)</Label>
          <Input value={f.exclude_titles} onChange={set("exclude_titles")} data-testid="agency-exclude-titles-input" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
        <Button variant="ghost" onClick={() => save().then(() => toast.success("Αποθηκεύτηκε")).catch((e) => toast.error(errMsg(e)))} disabled={busy} data-testid="agency-save-queries-btn">
          <Save className="w-4 h-4 mr-1" /> Αποθήκευση
        </Button>
        <Button variant="outline" onClick={() => run("cache")} disabled={busy} data-testid="agency-run-cache-btn">
          <Database className="w-4 h-4 mr-1" /> Από αποθηκευμένα (δωρεάν)
        </Button>
        <Button variant="outline" onClick={() => run("web")} disabled={busy} data-testid="agency-run-web-btn">
          <Globe className="w-4 h-4 mr-1" /> Internet ({usd(project.cost?.web.usd)})
        </Button>
        <Button variant="outline" onClick={() => run("both")} disabled={busy} data-testid="agency-run-both-btn">
          <Layers className="w-4 h-4 mr-1" /> Internet + Explee ({usd((project.cost?.web.usd || 0) + (project.cost?.search.usd || 0))})
        </Button>
        <Button onClick={() => run("explee")} disabled={busy} data-testid="agency-run-explee-btn">
          <Search className="w-4 h-4 mr-1" /> Αναζήτηση Explee (~{project.estimated_credits ?? 0} credits · {usd(project.cost?.search.usd)})
        </Button>
      </div>
    </div>
  );
};
